import { useQuery } from '@tanstack/react-query'
import { dataApiClient } from '../client'
import { staticCacheConfig } from '../../../lib/utils'
import type { SpecimenFilterOptionsResponse } from '../'

/**
 * Hook for fetching specimen filter options from the /specimen/filter-options endpoint
 * This endpoint provides all available filter options for specimens (e.g., specimen types, countries, diagnoses, etc.)
 */
export function useSpecimenFilterOptions(options?: { enabled?: boolean; staleTime?: number; gcTime?: number }) {
  return useQuery({
    queryKey: ['specimen-filter-options'],
    queryFn: async (): Promise<SpecimenFilterOptionsResponse> => {
      const response = await dataApiClient.GET('/specimen/filter-options')

      if (!response.data) {
        throw new Error(`Failed to fetch specimen filter options: ${JSON.stringify(response.error || 'Unknown error')}`)
      }

      return response.data
    },
    ...staticCacheConfig,
    ...options,
  })
}

/**
 * Hook for getting specific filter options by key
 * @param key - The filter key to get options for (e.g., 'specimen_type', 'country_of_origin', 'condition')
 */
export function useSpecimenFilterOptionsByKey(
  key: string,
  options?: {
    enabled?: boolean
    staleTime?: number
    gcTime?: number
  }
) {
  const { data, ...rest } = useSpecimenFilterOptions(options)

  return {
    ...rest,
    data: data?.filter_options?.options?.[key] || [],
    filterOptions: data?.filter_options,
  }
}
